import React, { useEffect, useState } from "react";
import { Input, InputNumber, Button, Switch } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate, useParams, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import firebase from "../../config/firebase";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import ModernAlertDialog from "../frete/components/ModernAlertDialog";
import { GlobalSuccessModal, GlobalRequiredFieldsModal } from "../../components/modalGlobal/modalGlobal";
import "./paymentCondition.css";

import "firebase/auth";
import "firebase/firestore";

const DEFAULT_OPTIONS = [
  "Boleto - A vista",
  "Boleto - 7 dias",
  "Boleto - 7/14 dias",
  "Boleto - 7/14/21 dias",
  "Boleto - 7/14/21/28 dias",
  "Boleto - 10 dias",
  "Boleto - 10/20 dias",
  "Boleto - 10/20/30 dias",
  "Boleto - 10/20/30/40 dias",
  "Boleto - 15 dias",
  "Boleto - 15/30 dias",
  "Boleto - 15/30/45 dias",
  "Boleto - 15/30/45/60 dias",
  "Boleto - 20 dias",
  "Boleto - 20/40 dias",
  "Boleto - 21 dias",
  "Boleto - 21/42 dias",
  "Boleto - 28 Dias",
  "Boleto - 30 dias",
  "Boleto - 30/60 dias",
  "Boleto - 30/45/60 dias",
  "Boleto - 40 Dias",
  "Boleto - 45 Dias",
  "Boleto - 60 Dias",
];

const createInstallment = (days = null, percentage = null) => ({
  days,
  percentage,
});

const roundPercentage = (value) => Number((Number(value) || 0).toFixed(2));

const formatPercentage = (value) => {
  const rounded = roundPercentage(value);
  return rounded.toLocaleString("pt-BR", {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const parseDaysFromDescription = (descriptionValue) => {
  const normalized = String(descriptionValue || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (!normalized) return [];
  if (normalized.includes("vista")) return [0];

  const match = normalized.match(/(\d+(?:\s*\/\s*\d+)*)\s*dias?/i);
  if (!match?.[1]) return [];

  return match[1]
    .split("/")
    .map((part) => Number(String(part).trim()))
    .filter((value) => Number.isFinite(value) && value >= 0);
};

const buildInstallmentsFromDescription = (descriptionValue) => {
  const days = parseDaysFromDescription(descriptionValue);
  if (days.length === 0) return null;

  const basePercentage = Math.floor((100 / days.length) * 100) / 100;

  return days.map((dayCount, index) =>
    createInstallment(
      dayCount,
      index === days.length - 1
        ? roundPercentage(100 - basePercentage * (days.length - 1))
        : basePercentage
    )
  );
};

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeInstallmentsForState = (conditionData) => {
  const savedInstallments = Array.isArray(conditionData?.paymentInstallments)
    ? conditionData.paymentInstallments
    : [];

  if (savedInstallments.length > 0) {
    return savedInstallments.map((installment) =>
      createInstallment(
        toOptionalNumber(installment?.days),
        toOptionalNumber(installment?.percentage)
      )
    );
  }

  return buildInstallmentsFromDescription(conditionData?.description) || [
    createInstallment(),
  ];
};

const isFilledNumber = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value));

const sanitizeInstallments = (installments) =>
  installments.map((installment) => ({
    days: Number(installment.days),
    percentage: roundPercentage(installment.percentage),
  }));

function PaymentConditionForm() {
  const db = firebase.firestore();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const userLogado = useSelector((state) => state.usuarioLogado);
  const user = useSelector((state) => state.user);

  const canCreate = user?.permissoes?.paymentCondition?.isCreate === true;
  const canEdit = user?.permissoes?.paymentCondition?.isEdit === true;
  const canView = user?.permissoes?.paymentCondition?.isView === true;
  const isEditRoute = Boolean(id);
  const isNewRoute = !id;

  const [view, setOnlyView] = useState(
    () =>
      location.state?.viewOnly === true ||
      (Boolean(id) && !canEdit && canView),
  );
  const [description, setDescription] = useState("");
  const [observation, setObservation] = useState("");
  const [active, setActive] = useState(true);
  const [paymentInstallments, setPaymentInstallments] = useState(() => [
    createInstallment(),
  ]);
  const [loading, setLoading] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [openOptionsModal, setOpenOptionsModal] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [infoDialogTitle, setInfoDialogTitle] = useState("");
  const [infoDialogMsg, setInfoDialogMsg] = useState("");
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalTitle, setSuccessModalTitle] = useState("");
  const [showRequiredFieldsModal, setShowRequiredFieldsModal] = useState(false);
  const [requiredFieldsMessages, setRequiredFieldsMessages] = useState([]);

  const currentShipperId = user?.uidShipper || null;
  const percentageTotal = roundPercentage(
    paymentInstallments.reduce(
      (total, installment) => total + (Number(installment.percentage) || 0),
      0
    )
  );
  const isPercentageTotalValid = percentageTotal === 100;

  const getInstallmentValidationMessages = () => {
    const errors = [];

    if (!paymentInstallments.length) {
      errors.push("Informe ao menos uma parcela.");
      return errors;
    }

    paymentInstallments.forEach((installment, index) => {
      const installmentLabel = `Parcela ${index + 1}`;
      const days = Number(installment.days);
      const percentage = Number(installment.percentage);

      if (!isFilledNumber(installment.days)) {
        errors.push(`${installmentLabel}: informe os dias para vencimento.`);
      } else if (days < 0 || !Number.isInteger(days)) {
        errors.push(`${installmentLabel}: os dias devem ser um numero inteiro maior ou igual a 0.`);
      }

      if (!isFilledNumber(installment.percentage)) {
        errors.push(`${installmentLabel}: informe o percentual da parcela.`);
      } else if (percentage < 0 || percentage > 100) {
        errors.push(`${installmentLabel}: o percentual deve estar entre 0 e 100.`);
      }
    });

    if (!isPercentageTotalValid) {
      errors.push(
        `O total das parcelas deve ser exatamente 100%. Atualmente: ${formatPercentage(percentageTotal)}%.`
      );
    }

    return errors;
  };

  const installmentValidationMessages = getInstallmentValidationMessages();
  const isSaveDisabled = loading || !isPercentageTotalValid;

  useEffect(() => {
    const unsubscribe = db.collection("paymentCondition").onSnapshot((snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setConditions(data);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    const loadCondition = async () => {
      try {
        const doc = await db.collection("paymentCondition").doc(id).get();
        if (!cancelled && doc.exists) {
          const data = doc.data();
          setDescription(data.description || "");
          setObservation(data.observation || "");
          setActive(data.active ?? true);
          setPaymentInstallments(normalizeInstallmentsForState(data));
        }
      } catch (error) {
        console.error("Erro ao carregar condicao de pagamento:", error);
      }
    };

    loadCondition();

    return () => {
      cancelled = true;
    };
  }, [db, id]);

  const handleInstallmentChange = (index, field, value) => {
    setPaymentInstallments((prev) =>
      prev.map((installment, installmentIndex) =>
        installmentIndex === index
          ? { ...installment, [field]: toOptionalNumber(value) }
          : installment
      )
    );
  };

  const handleAddInstallment = () => {
    setPaymentInstallments((prev) => [...prev, createInstallment()]);
  };

  const handleRemoveInstallment = (index) => {
    setPaymentInstallments((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, installmentIndex) => installmentIndex !== index);
    });
  };

  const handleApplyDefaultOption = (option) => {
    setDescription(option);

    const installmentsFromOption = buildInstallmentsFromDescription(option);
    if (installmentsFromOption) {
      setPaymentInstallments(installmentsFromOption);
    }

    setOpenOptionsModal(false);
  };

  const validateRequiredFields = () => {
    const errors = [];
    if (!description || !description.trim()) {
      errors.push("Descrição é obrigatória.");
    }
    if ((observation || "").length > 200) {
      errors.push("Observações deve ter no máximo 200 caracteres.");
    }
    errors.push(...installmentValidationMessages);

    if (errors.length > 0) {
      setRequiredFieldsMessages(errors);
      setShowRequiredFieldsModal(true);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (view) return;

    if (!validateRequiredFields()) return;

    setLoading(true);

    try {
      const paymentInstallmentsPayload =
        sanitizeInstallments(paymentInstallments);
      const payload = {
        description: description.trim(),
        observation: observation || "",
        active,
        uidShipper: currentShipperId,
        installments: paymentInstallmentsPayload.length,
        paymentInstallments: paymentInstallmentsPayload,
      };

      if (id) {
        await db
          .collection("paymentCondition")
          .doc(id)
          .update({ ...payload, updated_at: new Date() });
      } else {
        await db.collection("paymentCondition").add({
          ...payload,
          created_at: new Date(),
        });
      }

      setSuccessModalTitle(
        id
          ? "Condição de pagamento atualizada com sucesso!"
          : "Condição de pagamento cadastrada com sucesso!"
      );
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Erro ao salvar condicao de pagamento:", error);
      setInfoDialogTitle("Erro ao salvar");
      setInfoDialogMsg("Ocorreu um erro ao salvar a condicao de pagamento. Tente novamente.");
      setOpenInfoDialog(true);
    } finally {
      setLoading(false);
    }
  };

  if (userLogado <= 0) {
    return <Navigate to="/login" />;
  }

  if (isNewRoute && !canCreate) {
    return <Navigate to="/paymentCondition" replace />;
  }

  if (isEditRoute && !canEdit && !canView) {
    return <Navigate to="/paymentCondition" replace />;
  }

  const pageTitle = isEditRoute
    ? view
      ? "Visualizar Condicao de Pagamento"
      : "Editar Condicao de Pagamento"
    : "Cadastro de Condicoes de Pagamento";

  return (
    <NewMiniDrawer
      divOpen={
        <div className="freight-content">
          <div className="container">
            <h4>{pageTitle}</h4>

            <div className="row mt-4">
              <div className="col-md-6">
                Descricao <span style={{ color: "red" }}>*</span>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Informe a descricao"
                  disabled={view}
                />
              </div>

              {!view ? (
                <div className="col-md-3 d-flex align-items-end">
                  <Button className="btn-form-fortio" onClick={() => setOpenOptionsModal(true)}>
                    Modelos
                  </Button>
                </div>
              ) : (
                <div className="col-md-3" />
              )}

              <div className="col-md-3">
                <label>Ativo</label>
                <br />
                <Switch
                  checked={active}
                  onChange={setActive}
                  disabled={view}
                  style={{
                    backgroundColor: active ? "#012443" : "#d9d9d9",
                  }}
                />
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-md-12">
                <label>Observacoes</label>
                <Input.TextArea
                  rows={3}
                  maxLength={200}
                  showCount={!view}
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  disabled={view}
                />
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-md-12">
                <div className="payment-installments-section">
                  <div className="payment-installments-title-row">
                    <h5>Parcelas do boleto</h5>

                    {!view ? (
                      <Button
                        className="btn-form-fortio"
                        icon={<PlusOutlined />}
                        onClick={handleAddInstallment}
                      >
                        Adicionar parcela
                      </Button>
                    ) : null}
                  </div>

                  <div className="payment-installments-table">
                    <div className="payment-installments-row payment-installments-head">
                      <div>Parcela</div>
                      <div>Dias para vencimento</div>
                      <div>Percentual da parcela</div>
                      <div />
                    </div>

                    {paymentInstallments.map((installment, index) => (
                      <div className="payment-installments-row" key={index}>
                        <div className="payment-installment-number">
                          {index + 1}
                        </div>

                        <InputNumber
                          min={0}
                          precision={0}
                          value={installment.days}
                          onChange={(value) =>
                            handleInstallmentChange(index, "days", value)
                          }
                          placeholder="Dias"
                          disabled={view}
                          style={{ width: "100%" }}
                        />

                        <InputNumber
                          min={0}
                          max={100}
                          precision={2}
                          value={installment.percentage}
                          onChange={(value) =>
                            handleInstallmentChange(index, "percentage", value)
                          }
                          placeholder="Percentual"
                          disabled={view}
                          addonAfter="%"
                          decimalSeparator=","
                          style={{ width: "100%" }}
                        />

                        {!view ? (
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveInstallment(index)}
                            disabled={paymentInstallments.length <= 1}
                            aria-label={`Remover parcela ${index + 1}`}
                          />
                        ) : (
                          <div />
                        )}
                      </div>
                    ))}
                  </div>

                  <div
                    className={`payment-installments-total ${
                      isPercentageTotalValid ? "is-valid" : "is-invalid"
                    }`}
                  >
                    Total configurado: {formatPercentage(percentageTotal)}%
                  </div>

                  {!isPercentageTotalValid ? (
                    <div className="payment-installments-error">
                      O total das parcelas deve ser exatamente 100%. Atualmente:{" "}
                      {formatPercentage(percentageTotal)}%.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-md-12 d-flex gap-2">
                <Button
                  className="btn-form-fortio btn-back-fortio"
                  onClick={() => navigate("/paymentCondition")}
                >
                  Voltar
                </Button>

                {!view ? (
                  <>
                    <Button className="btn-form-fortio" onClick={() => setOpenCancelDialog(true)}>
                      Cancelar
                    </Button>

                    <Button
                      className="btn-form-fortio"
                      loading={loading}
                      disabled={isSaveDisabled}
                      onClick={handleSave}
                    >
                      Salvar
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          <ModernAlertDialog
            open={openInfoDialog}
            handleClose={() => {
              setOpenInfoDialog(false);
              if (
                infoDialogTitle === "Condicao de pagamento atualizada" ||
                infoDialogTitle === "Condicao de pagamento cadastrada"
              ) {
                navigate("/paymentCondition");
              }
            }}
            title={infoDialogTitle}
            msg={infoDialogMsg}
          />

          <ModernAlertDialog
            open={openCancelDialog}
            handleClose={() => setOpenCancelDialog(false)}
            title="Cancelar cadastro"
            msg="Deseja cancelar? As alteracoes nao salvas serao perdidas."
            showCancel
            confirmLabel="Confirmar"
            cancelLabel="Voltar"
            onConfirm={() => navigate("/paymentCondition")}
            onCancel={() => setOpenCancelDialog(false)}
          />

          <Dialog
            open={openOptionsModal}
            onClose={() => setOpenOptionsModal(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Modelos de Condicoes de Pagamento</DialogTitle>
            <DialogContent dividers>
              <div style={{ display: "grid", gap: 8 }}>
                {DEFAULT_OPTIONS.map((option) => (
                  <Button
                    key={option}
                    onClick={() => handleApplyDefaultOption(option)}
                  >
                    {option}
                  </Button>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenOptionsModal(false)} danger>
                Fechar
              </Button>
            </DialogActions>
          </Dialog>
          <GlobalSuccessModal
            open={showSuccessModal}
            handleClose={() => {
              setShowSuccessModal(false);
              navigate("/paymentCondition");
            }}
            title={successModalTitle}
          />
          <GlobalRequiredFieldsModal
            open={showRequiredFieldsModal}
            handleClose={() => setShowRequiredFieldsModal(false)}
            messages={requiredFieldsMessages}
          />
        </div>
      }
    />
  );
}

export default PaymentConditionForm;
